/** Swaps list (Phase 8). Also used on a user's page. Read-only: swaps complete on their own. */
import type { AdminSwap } from "@custos/api-client";
import {
  Badge,
  Card,
  EmptyState,
  ExternalLink,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  formatAmount,
  formatBps,
  formatDateTime,
  type Tone,
} from "@custos/ui";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { errorMessage } from "../lib/errors";
import { usd } from "../lib/format";
import { useSwaps, useLimits } from "../lib/queries";
import { Pager } from "./Users";

const TONE: Record<AdminSwap["status"], Tone> = {
  QUOTED: "neutral",
  EXECUTING: "primary",
  COMPLETED: "success",
  FAILED: "danger",
  EXPIRED: "neutral",
};

export function SwapsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "ALL";
  const limits = useLimits();
  const s = limits.data?.swaps;
  return (
    <>
      <PageHeader title="Swaps" subtitle="Same-network swaps inside users' accounts." />
      {s && (
        <p
          className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted"
          data-testid="swap-settings-summary"
        >
          <Badge tone={s.paused ? "danger" : "success"}>{s.paused ? "Swaps PAUSED" : "Swaps running"}</Badge>
          Max per swap {usd(s.policy.maxPerSwapUsdCents)} · fee {formatBps(s.policy.feeBps)} ·{" "}
          <Link to="/limits" className="text-fg hover:underline">
            change on Limits
          </Link>
        </p>
      )}
      <Segmented
        label="Filter"
        value={status}
        onChange={(v) => setParams(v === "ALL" ? {} : { status: v }, { replace: true })}
        className="mb-4"
        options={[
          { value: "ALL", label: "All" },
          { value: "COMPLETED", label: "Completed" },
          { value: "FAILED", label: "Failed" },
        ]}
      />
      <SwapList status={status} />
    </>
  );
}

export function SwapList({ status = "ALL", userId }: { status?: string; userId?: string }) {
  const [page, setPage] = useState(0);
  const list = useSwaps(status, userId, page);

  if (list.isPending) return <Skeleton className="h-40 rounded-card" />;
  if (list.error) return <Notice tone="danger">{errorMessage(list.error)}</Notice>;
  if (list.data.swaps.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Spot name="history" />} title="No swaps" />
      </Card>
    );
  }
  return (
    <>
      <ul className="stagger flex flex-col gap-3" data-testid="swap-list">
        {list.data.swaps.map((s) => (
          <li key={s.id} data-testid="swap-row">
            <Card className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-lg text-fg tabular-nums">
                  {formatAmount(s.fromAmount, 8)} <span className="text-muted">{s.fromAsset}</span> →{" "}
                  {formatAmount(s.toAmount, 8)} <span className="text-muted">{s.toAsset}</span>
                </span>
                <Badge>{s.chain}</Badge>
                <Badge tone={TONE[s.status]}>{s.status.toLowerCase()}</Badge>
                <span className="text-sm text-muted">{usd(s.usdValueCents, "no price")}</span>
              </div>
              <p className="text-sm text-muted">
                <Link to={`/users/${s.userId}`} className="text-fg hover:underline">
                  {s.userEmail}
                </Link>{" "}
                · {formatDateTime(s.createdAt)} ·{" "}
                {s.provider === "mock" ? "testnet mock (ledger only)" : s.provider} · impact{" "}
                {formatBps(s.priceImpactBps)} · slippage {formatBps(s.slippageBps)}
                {s.feeAmount !== "0" && ` · fee ${formatAmount(s.feeAmount, 8)} ${s.toAsset}`}
              </p>
              {s.explorerUrl && <ExternalLink href={s.explorerUrl}>Explorer</ExternalLink>}
              {s.failureReason && <p className="text-sm text-warning">{s.failureReason}</p>}
            </Card>
          </li>
        ))}
      </ul>
      <Pager page={page} total={list.data.total} pageSize={list.data.pageSize} onPage={setPage} />
    </>
  );
}
